import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Loader2, CheckCircle, Crown, Users, Building2, Sparkles,
  ArrowLeft, ArrowRight, Phone, Upload, ShieldCheck, Clock, Info,
  FileText, Copy, CheckCheck
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => n != null ? `₱${Number(n).toLocaleString('en-PH', { minimumFractionDigits: 2 })}` : 'Custom';

const SubscriptionPage = () => {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [orders, setOrders] = useState([]);

  // Flow state: 'plans' → 'checkout' → 'payment'
  const [view, setView] = useState('plans');
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('gcash');
  const [contactNumber, setContactNumber] = useState('');
  const [placing, setPlacing] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [pkgRes, statusRes, methodsRes, ordersRes] = await Promise.all([
        subscriptionAPI.getPackages(),
        subscriptionAPI.getStatus(),
        subscriptionAPI.getPaymentMethods(),
        subscriptionAPI.getOrders(),
      ]);
      setPackages(pkgRes.data);
      setStatus(statusRes.data);
      setPaymentMethods(methodsRes.data);
      setOrders(ordersRes.data);
    } catch (err) {
      console.error('Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (pkg) => {
    const monthly = pkg.monthly_price_php;
    if (!monthly) return null;
    if (billingCycle === 'annual') return monthly * 12 * (1 - pkg.annual_discount_percent / 100);
    return monthly;
  };

  const handleSelectPlan = (pkg) => {
    setSelectedPkg(pkg);
    setSelectedMethod(paymentMethods[0]?.code || 'gcash');
    setView('checkout');
  };

  const handlePlaceOrder = async () => {
    setPlacing(true);
    try {
      const res = await subscriptionAPI.createOrder({
        package_code: selectedPkg.code,
        billing_cycle: billingCycle,
        payment_method: selectedMethod,
        contact_number: contactNumber || null,
      });
      setCurrentOrder(res.data);
      setView('payment');
      toast.success('Order placed! Please complete your payment.');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place order');
    } finally {
      setPlacing(false);
    }
  };

  const handleUploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await subscriptionAPI.uploadProof(currentOrder.id, file);
      toast.success('Proof of payment uploaded! We will review it shortly.');
      const updated = await subscriptionAPI.getOrder(currentOrder.id);
      setCurrentOrder(updated.data);
      fetchData();
    } catch (err) {
      toast.error('Upload failed — please try again');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const viewExistingOrder = async (order) => {
    try {
      const res = await subscriptionAPI.getOrder(order.id);
      setCurrentOrder(res.data);
      setView('payment');
    } catch { toast.error('Failed to load order'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" /></div>;
  }

  const currentPkg = status?.package_code;
  const pendingOrder = orders.find(o => o.status === 'pending' || o.status === 'proof_uploaded');

  // ============ CHECKOUT VIEW ============
  if (view === 'checkout' && selectedPkg) {
    const price = getPrice(selectedPkg);
    const method = paymentMethods.find(m => m.code === selectedMethod);

    return (
      <div className="max-w-4xl mx-auto space-y-6" data-testid="checkout-view">
        <button onClick={() => setView('plans')} className="flex items-center gap-2 text-[#5A6570] hover:text-[#1A1D21] transition-colors text-sm">
          <ArrowLeft className="h-4 w-4" />Back to Plans
        </button>
        <h1 className="text-2xl font-bold text-[#1A1D21]">Checkout</h1>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Order Summary */}
          <div className="lg:col-span-2">
            <Card className="border-[#E2E6EA]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-[#1A1D21]">{selectedPkg.name}</p>
                    <p className="text-xs text-[#5A6570]">
                      {billingCycle === 'annual' ? 'Annual' : 'Monthly'} billing
                    </p>
                  </div>
                  <p className="font-semibold text-[#1A1D21]">{fmt(price)}</p>
                </div>
                <div className="border-t border-[#E2E6EA] pt-3 flex justify-between">
                  <p className="font-bold text-[#1A1D21]">Total</p>
                  <p className="font-bold text-lg text-[#F58A26]">{fmt(price)}</p>
                </div>
                {billingCycle === 'annual' && (
                  <p className="text-xs text-green-600">Save {selectedPkg.annual_discount_percent}% with annual billing</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Payment & Contact */}
          <div className="lg:col-span-3 space-y-4">
            <Card className="border-[#E2E6EA]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#00A3A3]" />Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {paymentMethods.map(m => (
                  <button
                    key={m.code}
                    type="button"
                    onClick={() => setSelectedMethod(m.code)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                      selectedMethod === m.code
                        ? 'border-[#00A3A3] bg-[#00A3A3]/5'
                        : 'border-[#E2E6EA] hover:border-[#A8B1B9]'
                    }`}
                    data-testid={`payment-method-${m.code}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedMethod === m.code ? 'border-[#00A3A3]' : 'border-[#A8B1B9]'
                    }`}>
                      {selectedMethod === m.code && <div className="w-2.5 h-2.5 rounded-full bg-[#00A3A3]" />}
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A1D21]">{m.name}</p>
                      <p className="text-xs text-[#5A6570]">{m.account_number}</p>
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>

            <Card className="border-[#E2E6EA]">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Phone className="h-4 w-4 text-[#00A3A3]" />Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label className="text-sm">Contact Number <span className="text-[#A8B1B9]">(Optional)</span></Label>
                  <Input
                    placeholder="09XX-XXX-XXXX"
                    value={contactNumber}
                    onChange={(e) => setContactNumber(e.target.value)}
                    className="border-[#E2E6EA]"
                    data-testid="contact-number-input"
                  />
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={handlePlaceOrder}
              disabled={placing || !selectedMethod}
              className="w-full h-12 bg-[#F58A26] hover:bg-[#E07A1C] text-base font-semibold"
              data-testid="place-order-btn"
            >
              {placing ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : null}
              Place Order <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <p className="text-xs text-[#A8B1B9] text-center">By placing this order, you agree to our Terms of Use and Privacy Policy.</p>
          </div>
        </div>
      </div>
    );
  }

  // ============ PAYMENT VIEW ============
  if (view === 'payment' && currentOrder) {
    const pi = currentOrder.payment_instructions;
    const isUploaded = currentOrder.status === 'proof_uploaded';
    const isApproved = currentOrder.status === 'approved';
    const isRejected = currentOrder.status === 'rejected';

    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="payment-view">
        <button onClick={() => { setView('plans'); setCurrentOrder(null); }} className="flex items-center gap-2 text-[#5A6570] hover:text-[#1A1D21] transition-colors text-sm">
          <ArrowLeft className="h-4 w-4" />Back to Plans
        </button>
        <h1 className="text-2xl font-bold text-[#1A1D21]">Complete Your Payment</h1>

        {/* Order Summary Banner */}
        <div className="bg-[#FFF8ED] border-l-4 border-[#F58A26] p-4 rounded-r-lg">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm font-semibold text-[#3A3F45]">Order ID: </span>
              <span className="font-mono text-[#1A1D21]">{currentOrder.order_id}</span>
            </div>
            <div>
              <span className="text-sm font-semibold text-[#3A3F45]">Total: </span>
              <span className="font-bold text-[#1A1D21]">{fmt(currentOrder.amount_php)}</span>
            </div>
          </div>
        </div>

        {/* Payment Instructions */}
        {pi && (
          <Card className="border-[#E2E6EA] bg-[#FAFBFC]">
            <CardContent className="p-5 space-y-3">
              <h3 className="font-semibold text-[#1A1D21]">{pi.name} Payment Instructions</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#5A6570]">Account Name:</span>
                  <span className="font-medium text-[#1A1D21]">{pi.account_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#5A6570]">Account Number:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium text-[#1A1D21]">{pi.account_number}</span>
                    <button
                      onClick={() => copyToClipboard(pi.account_number, 'acct')}
                      className="text-[#00A3A3] hover:text-[#008585] transition-colors"
                      data-testid="copy-account-number"
                    >
                      {copied === 'acct' ? <CheckCheck className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-sm text-[#3A3F45] bg-white/60 p-3 rounded-lg border border-[#E2E6EA]">
                Send payment via {pi.name} to <strong>{pi.account_number}</strong> ({pi.account_name}).
                Include your Order ID <strong>{currentOrder.order_id}</strong> in the message.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Status Messages */}
        {isApproved && (
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-800">Payment Approved!</p>
              <p className="text-sm text-green-700">Your subscription has been activated.</p>
            </div>
          </div>
        )}
        {isRejected && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <p className="font-semibold text-red-800">Payment Rejected</p>
            <p className="text-sm text-red-700">{currentOrder.admin_notes || 'Please contact support or try again.'}</p>
          </div>
        )}

        {/* Upload Proof */}
        {!isApproved && !isRejected && (
          <Card className="border-[#E2E6EA]">
            <CardContent className="p-5 space-y-4">
              <h3 className="font-semibold text-[#1A1D21]">Upload Proof of Payment</h3>
              <input
                ref={fileRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                onChange={handleUploadProof}
                className="hidden"
                data-testid="proof-upload-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className={`w-full border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isUploaded ? 'border-green-300 bg-green-50' : 'border-[#E2E6EA] hover:border-[#00A3A3] hover:bg-[#00A3A3]/5'
                }`}
                data-testid="proof-upload-area"
              >
                {uploading ? (
                  <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin text-[#00A3A3]" />
                ) : isUploaded ? (
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                ) : (
                  <Upload className="h-8 w-8 mx-auto mb-2 text-[#A8B1B9]" />
                )}
                <p className="font-medium text-[#1A1D21]">
                  {isUploaded ? 'Proof uploaded — click to replace' : 'Click to upload your payment screenshot/receipt'}
                </p>
                <p className="text-xs text-[#A8B1B9] mt-1">PDF or Image files (JPG, PNG, WEBP)</p>
              </button>
              {isUploaded && (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg">
                  <CheckCircle className="h-4 w-4" />
                  <span>Proof uploaded — waiting for admin review</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Data Retention Notice */}
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-sm text-blue-800">
          <p className="font-semibold flex items-center gap-2"><Info className="h-4 w-4" />Data Retention Notice:</p>
          <p className="mt-1">Proofs of payment will be stored for 10 days from the delivery/completion date for verification purposes. After this period, the admin will delete them to protect your privacy.</p>
        </div>

        <p className="text-xs text-[#5A6570]">Make sure your proof of payment clearly shows the transaction details including the amount and reference number.</p>
      </div>
    );
  }

  // ============ PLANS VIEW ============
  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1A1D21]">Subscription</h1>
        <p className="text-[#5A6570] mt-1">Choose the plan that fits your governance needs</p>
      </div>

      {/* Current Status */}
      {status && (
        <Card className={`border-2 ${status.is_expired ? 'border-red-400 bg-red-50' : status.subscription_status === 'trial' ? 'border-blue-300 bg-blue-50' : 'border-green-300 bg-green-50'}`}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="font-medium text-[#1A1D21]">
                {status.subscription_status === 'trial' ? 'Free Trial' : 'Current Plan'}: <strong>{status.package_name}</strong>
              </p>
              {status.days_remaining != null && (
                <p className={`text-sm ${status.is_expired ? 'text-red-600 font-bold' : 'text-blue-700'}`}>
                  {status.is_expired
                    ? 'Trial expired — please select a plan to continue'
                    : `${status.days_remaining} days remaining in trial`
                  }
                </p>
              )}
            </div>
            <Badge className={`${
              status.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
              status.subscription_status === 'trial' ? 'bg-blue-100 text-blue-800' :
              'bg-red-100 text-red-800'
            }`}>
              {status.subscription_status}
            </Badge>
          </CardContent>
        </Card>
      )}

      {/* Pending Order Banner */}
      {pendingOrder && (
        <Card className="border-2 border-[#F58A26] bg-[#FFF8ED]">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="font-medium text-[#1A1D21]">
                <Clock className="h-4 w-4 inline mr-1 text-[#F58A26]" />
                Pending Order: <strong>{pendingOrder.package_name}</strong> ({fmt(pendingOrder.amount_php)})
              </p>
              <p className="text-sm text-[#5A6570]">
                {pendingOrder.status === 'proof_uploaded' ? 'Proof uploaded — awaiting admin review' : 'Please complete your payment'}
              </p>
            </div>
            <Button size="sm" className="bg-[#F58A26] hover:bg-[#E07A1C]" onClick={() => viewExistingOrder(pendingOrder)} data-testid="continue-payment-btn">
              {pendingOrder.status === 'pending' ? 'Complete Payment' : 'View Order'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Billing Toggle */}
      <div className="flex justify-center gap-2 items-center">
        <Button
          variant={billingCycle === 'monthly' ? 'default' : 'outline'} size="sm"
          onClick={() => setBillingCycle('monthly')}
          className={billingCycle === 'monthly' ? 'bg-[#00A3A3]' : ''}
        >Monthly</Button>
        <Button
          variant={billingCycle === 'annual' ? 'default' : 'outline'} size="sm"
          onClick={() => setBillingCycle('annual')}
          className={billingCycle === 'annual' ? 'bg-[#00A3A3]' : ''}
        >Annual (Save up to 30%)</Button>
      </div>

      {/* Packages Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {packages.map(pkg => {
          const isCurrent = currentPkg === pkg.code;
          const price = getPrice(pkg);

          return (
            <Card key={pkg.id} className={`border-2 ${isCurrent ? 'border-[#00A3A3] ring-2 ring-[#00A3A3]/20' : 'border-[#E2E6EA]'} relative`}>
              {isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#00A3A3] text-white">Current Plan</Badge>
                </div>
              )}
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-lg">{pkg.name}</CardTitle>
                <CardDescription className="text-xs">{pkg.description}</CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div>
                  {price != null ? (
                    <>
                      <span className="text-3xl font-bold text-[#1A1D21]">{fmt(Math.round(price))}</span>
                      <span className="text-sm text-[#5A6570]">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span>
                      {billingCycle === 'annual' && pkg.monthly_price_php && (
                        <p className="text-xs text-green-600 mt-1">Save {pkg.annual_discount_percent}% annually</p>
                      )}
                    </>
                  ) : (
                    <span className="text-2xl font-bold text-[#1A1D21]">Custom Quote</span>
                  )}
                </div>

                <ul className="text-left text-sm text-[#3A3F45] space-y-2">
                  <li className="flex items-center gap-2"><Users className="h-4 w-4 text-[#00A3A3]" />{pkg.included_governance_users} governance users</li>
                  <li className="flex items-center gap-2"><Building2 className="h-4 w-4 text-[#00A3A3]" />{pkg.included_entities ?? 'Unlimited'} {pkg.included_entities === 1 ? 'entity' : 'entities'}</li>
                  {pkg.feature_flags?.asm_ssm && <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" />ASM/SSM workflows</li>}
                  {pkg.feature_flags?.ai_minutes && <li className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#F58A26]" />AI assistance</li>}
                  {pkg.feature_flags?.elections && <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" />Elections module</li>}
                  {pkg.feature_flags?.pdf_export && <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" />PDF export</li>}
                </ul>

                {isCurrent ? (
                  <Button disabled className="w-full" variant="outline">Current Plan</Button>
                ) : price != null ? (
                  <Button
                    className="w-full bg-[#F58A26] hover:bg-[#E07A1C]"
                    onClick={() => handleSelectPlan(pkg)}
                    data-testid={`select-${pkg.code}`}
                  >
                    <Crown className="h-4 w-4 mr-2" />Select Plan
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full border-[#00A3A3] text-[#00A3A3]">Contact Sales</Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Order History */}
      {orders.length > 0 && (
        <Card className="border-[#E2E6EA]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Order History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {orders.map(o => (
                <div key={o.id} className="flex items-center justify-between p-3 rounded-lg bg-[#FAFBFC] border border-[#E2E6EA]">
                  <div>
                    <p className="text-sm font-medium text-[#1A1D21]">{o.package_name} — {fmt(o.amount_php)}</p>
                    <p className="text-xs text-[#5A6570]">Order #{o.order_id} · {new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge className={`text-xs ${
                    o.status === 'approved' ? 'bg-green-100 text-green-800' :
                    o.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    o.status === 'cancelled' ? 'bg-gray-100 text-gray-600' :
                    o.status === 'proof_uploaded' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {o.status.replace('_', ' ')}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SubscriptionPage;
